import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Stack,
    useMediaQuery,
} from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { campaignName } from 'c-main/Lib';
import { generatePath } from 'react-router-dom';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { campaignIcon } from 'c-main/icons';
import CampaignSchedule from 'c-main/Components/Schedule/CampaignSchedule/CampaignSchedule';
import DspCampaignSchedule from 'c-main/Components/Schedule/Dsp/DspCampaignSchedule';
import { usesInsertionOrderTargeting } from 'c-main/Lib';
import { useEntityPage } from 'c-hooks';
import PopSettingsForm from 'c-main/Components/Schedule/Components/PopSettings/PopSettingsForm';
import { CollapsibleTransparentCard, TransparentCard } from 'c-components';
import ReactGA from 'react-ga';
import { useEntityData } from 'c-data';
import { CampaignOverviewDetails, CampaignThumbnail } from 'c-main/Components';
import MediaTable from 'c-main/Components/Schedule/Components/MediaTable/MediaTable';
import { useBoolean } from 'react-hanger';
import Audience from 'c-main/Components/Schedule/Audience/Audience';
import ApiClient from 'c-data/apiClient';
import Sidebar from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar';
import { GridMenuIcon } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/styles';
import type { SystemSearchEntityResource } from 'c-system-search';
import UseAPIClientRequest from '../../../../../Hooks/useAPIClientRequest';

const CampaignSchedulePage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const { start, data, isLoading, error } = UseAPIClientRequest(ApiClient.Entities.Campaign.get);
    const {
        start: audienceListStart,
        data: audienceListData,
        isLoading: audienceListLoading,
        error: audienceListError,
    } = UseAPIClientRequest(ApiClient.Entities.Displays_AudienceVersion.list);
    const id = useEntityPage().id;
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    const addNameState = useBoolean(false);

    const detailsAndThumbnail = useMemo(
        () => (
            <Box display="flex" alignItems="center">
                <Box pr={2}>
                    <CampaignThumbnail campaign={campaign} size={80} preferredSize="medium" />
                </Box>
                <TransparentCard title={campaignName(campaign)} cardHeaderProps={{ sx: { pb: 0 } }}>
                    <CardContent>
                        <CampaignOverviewDetails id={id} buttons />
                    </CardContent>
                </TransparentCard>
            </Box>
        ),
        [campaign, id],
    );

    useEffect(() => {
        start(id, [
            'owners',
            'owners.audienceVersion',
            'owners.owner',
            'campaignMediaGroupName',
            'campaignMediaGroupName.campaignMediaGroupParameter',
        ]);
        audienceListStart({ perPage: 10000000 });
    }, [audienceListStart, id, start]);
    const refreshCampaignGroups = React.useCallback(
        () =>
            start(id, [
                'owners',
                'owners.audienceVersion',
                'owners.owner',
                'campaignMediaGroupName',
                'campaignMediaGroupName.campaignMediaGroupParameter',
            ]),
        [id, start],
    );

    const loading = useMemo(
        () => isLoading || audienceListLoading,
        [audienceListLoading, isLoading],
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

    const toggleMobileDrawer = React.useCallback(() => {
        setMobileDrawerOpen(prev => !prev);
    }, []);
    const startDate = useMemo(() => campaign?.date_start?.split('T')[0], [campaign?.date_start]);

    const endDate = useMemo(() => campaign?.date_end?.split('T')[0], [campaign?.date_end]);
    const isLastPlayedEditable = useMemo(
        () =>
            (campaign?.pca_end_setting ?? data?.data?.data?.pca_end_setting) === 'based_on_owners',
        [campaign?.pca_end_setting, data?.data?.data?.pca_end_setting],
    );

    const content = (
        <Stack gap={2} mt={2}>
            {detailsAndThumbnail}
            <Card elevation={0}>
                <Box pl={2}>
                    <CollapsibleTransparentCard
                        title="Modules.Main.Campaigns.CampaignSchedule.mediaTable.title"
                        titleTypographyProps={{ variant: 'subtitle1' }}
                    >
                        <MediaTable
                            campaignId={id}
                            startDate={startDate}
                            endDate={endDate}
                            addNameState={addNameState}
                            campaignMediaGroupNames={data?.data?.data?.campaignMediaGroupName ?? []}
                            campaignMediaGroupParameters={
                                data?.data?.data?.campaignMediaGroupName?.flatMap(
                                    group => group.campaignMediaGroupParameter ?? [],
                                ) ?? []
                            }
                            refreshCampaignGroups={refreshCampaignGroups}
                        />
                    </CollapsibleTransparentCard>
                </Box>
            </Card>
            <Card elevation={0}>
                <CardContent sx={{ py: 0, pb: '0 !important' }}>
                    <CollapsibleTransparentCard
                        title="Modules.Main.Campaigns.CampaignSchedule.popSettings.title"
                        titleTypographyProps={{ variant: 'subtitle1' }}
                    >
                        <PopSettingsForm campaignId={id} addNameState={addNameState} />
                    </CollapsibleTransparentCard>
                </CardContent>
            </Card>
            <Card elevation={0}>
                <Box pl={2}>
                    <CollapsibleTransparentCard
                        title="Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.schedule"
                        titleTypographyProps={{ variant: 'subtitle1' }}
                        defaultOpen
                    >
                        <CardContent sx={{ height: '80vh', overflow: 'hidden', display: 'flex' }}>
                            {/*
                              UL-7912 -- branch before either mounts. The direct view fires six
                              requests on mount and fills shared state with displays and line items,
                              so it must never render for a DSP campaign -- not even for the moment
                              before the campaign type has loaded.
                            */}
                            {campaign?.type == null ? (
                                <CircularProgress sx={{ m: 'auto' }} />
                            ) : usesInsertionOrderTargeting(campaign) ? (
                                <DspCampaignSchedule id={id} />
                            ) : (
                                <CampaignSchedule id={id} />
                            )}
                        </CardContent>
                    </CollapsibleTransparentCard>
                </Box>
            </Card>
            <Card elevation={0} sx={{ mb: 2 }}>
                <Box pl={2}>
                    <CollapsibleTransparentCard
                        title="Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.title"
                        titleTypographyProps={{ variant: 'subtitle1' }}
                    >
                        <CardContent sx={{ height: '80vh', overflow: 'hidden', display: 'flex' }}>
                            <Audience
                                data={data?.data?.data?.owners ?? []}
                                loading={loading}
                                listData={audienceListData?.data?.data ?? []}
                                campError={error}
                                audienceListError={audienceListError}
                                campStart={() =>
                                    start(id, [
                                        'owners',
                                        'owners.audienceVersion',
                                        'owners.owner',
                                        'campaignMediaGroupName',
                                        'campaignMediaGroupName.campaignMediaGroupParameter',
                                    ])
                                }
                                listStart={() => audienceListStart({ perPage: 10000000 })}
                                isLastPlayedEditable={isLastPlayedEditable}
                            />
                        </CardContent>
                    </CollapsibleTransparentCard>
                </Box>
            </Card>
        </Stack>
    );

    return (
        <>
            {isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'grey.300',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'background.paper',
                        }}
                    >
                        <IconButton onClick={toggleMobileDrawer} edge="start" aria-label="menu">
                            <GridMenuIcon />
                        </IconButton>
                    </Box>
                    <Sidebar
                        id={id}
                        mobileDrawerOpen={mobileDrawerOpen}
                        toggleMobileDrawer={toggleMobileDrawer}
                    />
                    <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>{content}</Box>
                </Box>
            )}
            {!isMobile && (
                <Box sx={{ display: 'flex' }}>
                    <Sidebar id={id} />
                    <Box component="main" sx={{ flexGrow: 1, pt: 2, px: 2 }}>
                        {content}
                    </Box>
                </Box>
            )}
        </>
    );
};

const PageBreadcrumbs: SystemSearchEntityResource<Campaign>['renderEntityBreadcrumbs'] = () => (
    <Box display="flex" />
);

const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignSchedulePage',
    component: CampaignSchedulePage,
    systemSearch: {
        title: 'Modules.Main.Campaigns.CampaignSchedule.metaTitle',
        type: 'entity',
        entityName: 'Campaign',
        icon: campaignIcon,
        route: PostAuthRoutes.Campaigns.CampaignSchedule,
        saveAsRecentPage: true,
        excludeFromSystemSearch: true,
        defaultIncludes: [
            // 'lineItems',
            // 'lineItems.owner',
            // 'lineItems.resolutions',
            // 'lineItems.fileTypes',
            'logSourceCampaignIdentifiers',
            'audienceVersions',
        ],
        permissions: [
            PermissionName.UniliveapiSchedulesRead,
            PermissionName.UniliveapiRulesRead,
            PermissionName.DisplaysLine_itemsRead,
            PermissionName.DisplaysScreensRead,
        ],
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
        renderEntityBreadcrumbs: PageBreadcrumbs,
        nameGen: (entity, id, t) =>
            `${campaignName(entity)} ${t('Modules.Main.Campaigns.CampaignSchedule.metaTitle')}`,
        generateEntityBreadcrumbs: (crumbs, t, entity, id) => [
            ...crumbs,
            {
                label: campaignName(entity),
                link: generatePath(PostAuthRoutes.Campaigns.CampaignOverview, { id }),
            },
            {
                label: 'Modules.Main.Campaigns.CampaignSchedule.metaTitle',
            },
        ],
    },
};

export default setup;
